import { useState, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../spinner/Spinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../api/ApiServices";
import DataTable from "react-data-table-component";
import CustomStyles from "../../components/utils/CustomStyles";
import { handleError } from "../../components/utils/AuthenticationCheck";
import formatDate from "../../components/utils/DateFormatter";

const HeadOfficePendingDeliveries = () => {
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState("");
    const navigate = useNavigate();

    const fetchDeliveries = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) throw new Error("No access token found.");

            const response = await axios.get(ApiServices.getHeadOfficePendingDeliveries, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            });

            const { status, data } = response.data;
            if (status === 200 && data) {
                setDeliveries(data.deliveries || []);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDeliveries();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilter = (e) => {
        setFilterText(e.target.value);
    };

    const columns = [
        {
            name: "Source Type",
            selector: (row) => row.source_type?.replace(/_/g, " ").toUpperCase() || "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Product",
            selector: (row) => row.product_name || "N/A",
            sortable: true,
            width: "180px",
        },
        {
            name: "Head Office",
            selector: (row) => row.head_office?.name || "N/A",
            sortable: true,
            width: "220px",
        },
        {
            name: "Quantity",
            selector: (row) => row.quantity || 0,
            sortable: true,
            width: "100px",
        },
        {
            name: "Status",
            selector: (row) => row.status || "N/A",
            sortable: true,
            width: "130px",
            cell: (row) => (
                <span className={row.status === "pending" ? "text-orange1 font-semibold" : "text-green-600 font-semibold"}>
                    {row.status?.toUpperCase()}
                </span>
            ),
        },
        {
            name: "Dispatcher",
            selector: (row) => row.dispatcher_name || "Not Assigned",
            width: "160px",
        },
        {
            name: "Dispatched At",
            selector: (row) => row.dispatched_at ? formatDate(row.dispatched_at) : "Pending",
            width: "180px",
        },
        {
            name: "Notes",
            selector: (row) => row.notes || "—",
            wrap: true,
            width: "220px",
        },
        {
            name: "Created At",
            selector: (row) => formatDate(row.created_at) || "N/A",
            width: "180px",
        },
    ];

    const filteredItems = deliveries.filter((item) =>
        item.product_name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.head_office?.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.source_type?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.status?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.dispatcher_name?.toLowerCase().includes(filterText.toLowerCase())
    );

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Head Office</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Pending Deliveries</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                <div className="pb-4 bg-white dark:bg-gray-900 mt-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            type="search"
                            className="block pl-4 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 min-w-[200px]"
                            placeholder="Search deliveries..."
                            value={filterText}
                            onChange={handleFilter}
                        />
                    </div>
                </div>

                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        customStyles={CustomStyles}
                        noDataComponent={<div className="p-4 text-center text-gray-500">No pending deliveries found</div>}
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default HeadOfficePendingDeliveries;
